import {Injectable} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {QlQueryResult} from 'features/core/shared/ql/builder/ql-query-builder';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {EntityStatusEnum} from 'features/core/shared/entity/enum/entity-status.enum';
import {ContractAgentEnum} from 'features/feature-crm/cm/enum/contract-agent-enum.enum';
import {ProvisionMessageSeverity} from '../../enum/provision-message-severity.enum';
import {parseCpRunMessageAgentId} from '../domain/cp-run-message-guidance';
import {CpRunAgentRef} from '../../interface/cp-run-agent-ref.interface';
import {CpRunMessageView} from '../../interface/cp-run-message-view.interface';
import {CpRunContractRunView} from '../../interface/cp-run-contract-run-view.interface';
import {CpRunAgentTotal} from '../../interface/cp-run-agent-total.interface';
import {CpRunTransactionTotals} from '../../interface/cp-run-transaction-totals.interface';
import {CpRunTransactionKind} from '../../interface/cp-run-transaction-kind.interface';
import {CpRunRowContract} from '../../interface/cp-run-row-contract.interface';

/**
 * Read-only QL lookups of the run review (run detail, tabs "Verträge", "Meldungen",
 * "Lauf-Meldungen", the two transaction tabs and the contract link of "Ereignisse"/"Sätze").
 *
 * Everything here READS. The detail panes built on it have no save path by construction — they
 * never touch a CRUD service, so no form template, no update and no delete can be triggered.
 *
 * ## Hauptvermittler role
 * The role is `ContractAgentEnum.main` of the cm feature (the role the engine reads the
 * Hauptvermittler from, `ProvisionCalculationService.resolveComponentAgents`). cp already imports
 * this enum in its DTOs (`provision-type-component.dto.ts`), so the lookup does the same instead of
 * keeping a local ordinal.
 *
 * ## Why the Hauptvermittler join is a stacked LEFT join and not `onlyFirst`
 * `onlyFirst` on a reverse join is defective under the simple pipe (fe_core_ql, interim rule
 * 2026-09-28) and was measured here to IGNORE its `filterCriteria`. The stacked reverse join
 * `cm.ContractAgent#contract` with `assignment = main` in its ON clause returns exactly one row per
 * Hauptvermittler; the callers de-duplicate per message / per contract run, so a contract with
 * several Hauptvermittler (M-003) shows all of them instead of a random one.
 */
@Injectable({providedIn: 'root'})
export class CpRunReviewLookupService {

    constructor(private qlService: QlService) {
    }

    // ── Messages ───────────────────────────────────────────────────────────────────────────────

    /**
     * One contract message (`cp.ProvisionContractRunMessage`) with its contract, event and agents.
     *
     * Agent precedence: the agent the message NAMES in `detailedMessage` (M-004: the agent WITHOUT an
     * account) beats the contract's Hauptvermittler — for a duplicate agent name those are the two
     * different people the operator must tell apart.
     */
    loadContractMessage(messageId: number): Observable<CpRunMessageView> {

        const queryName = 'cpRunReviewContractMessage';

        return this.execute({
            name: queryName,
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionContractRunMessage',
                as: 'e',
                joins: [
                    {
                        type: 'entity', name: 'provisionContractRun', as: 'pcr', joinType: 'left',
                        fields: [{name: 'id'}],
                        joins: [
                            {
                                type: 'entity', name: 'contract', as: 'c', joinType: 'left',
                                fields: [{name: 'id'}, {name: 'contractIdentifier'}],
                                joins: [{
                                    type: 'entity',
                                    name: 'cm.ContractAgent#contract',
                                    as: 'mca',
                                    joinType: 'left',
                                    fields: [{name: 'id'}],
                                    filterCriteria: [
                                        FilterCriteria.create('mca.assignment', FilterCriteria.cOperatorEqual,
                                            ContractAgentEnum.main)
                                    ],
                                    joins: [{
                                        type: 'entity', name: 'agent', as: 'mag', joinType: 'left',
                                            fields: [{name: 'id'}, {name: 'name'}]
                                    }]
                                }]
                            }
                        ]
                    },
                    {
                        type: 'entity', name: 'provisionContract', as: 'pc', joinType: 'left',
                        fields: [{name: 'startDate'}],
                        joins: [
                            {type: 'entity', name: 'provisionTypeContractEvent', as: 'pce', joinType: 'left',
                                fields: [{name: 'name'}]}
                        ]
                    }
                ]
            },
            filters: [FilterCriteria.create('e.id', FilterCriteria.cOperatorEqual, messageId)],
            sortings: [new Sorting('e.severity', false), new Sorting('e.id', true)]
        }, queryName).pipe(
            map(rows => this.toContractMessageViews(rows)),
            switchMap(views => views.length
                ? this.attachMessageAgents(views[0])
                : throwError(() => new Error('Contract message ' + messageId + ' not found')))
        );
    }

    /** One run message (`cp.ProvisionRunMessage`) with the agent account it names, if any. */
    loadRunMessage(messageId: number): Observable<CpRunMessageView> {

        const queryName = 'cpRunReviewRunMessage';

        return this.execute({
            name: queryName,
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionRunMessage',
                as: 'e',
                joins: [
                    {
                        type: 'entity', name: 'provisionAgent', as: 'pa', joinType: 'left',
                        fields: [{name: 'id'}, {name: 'entityStatus'}],
                        joins: [{type: 'entity', name: 'agent', as: 'ag', joinType: 'left', fields: [{name: 'id'},
                            {name: 'name'}]}]
                    }
                ]
            },
            filters: [FilterCriteria.create('e.id', FilterCriteria.cOperatorEqual, messageId)]
        }, queryName).pipe(
            switchMap(rows => {
                if (!rows.length) {
                    return throwError(() => new Error('Run message ' + messageId + ' not found'));
                }
                const row = rows[0];
                const view = this.toMessageBase(row);
                const accountAgentId = this.num(row['ag.id']);
                if (accountAgentId) {
                    view.agentSource = 'account';
                    view.agents = [{
                        agentId: accountAgentId,
                        agentName: row['ag.name'] ?? '',
                        provisionAgentId: this.num(row['pa.id']),
                        provisionAgentActive: this.isActive(row['pa.entityStatus'])
                    }];
                    return of(view);
                }
                return this.attachMessageAgents(view);
            })
        );
    }

    // ── Contract run ("Verträge") ──────────────────────────────────────────────────────────────

    /**
     * The review of ONE contract in ONE run: outcome, contract, Hauptvermittler, every message of
     * that contract run (severest first) and the bookings this run produced for that contract.
     */
    loadContractRun(contractRunId: number): Observable<CpRunContractRunView> {

        const runQuery = 'cpRunReviewContractRun';
        const messageQuery = 'cpRunReviewContractRunMessages';

        return this.qlService.builder()
            .showErrors(false)
            .query({
                name: 'cpRunReviewContractRun',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cp.ProvisionContractRun',
                    as: 'e',
                    joins: [
                        {
                            type: 'entity', name: 'contract', as: 'c', joinType: 'left',
                            fields: [{name: 'id'}, {name: 'contractIdentifier'}],
                            joins: [
                                {
                                    type: 'entity',
                                    name: 'contractType',
                                    as: 'ct',
                                    joinType: 'left',
                                    fields: [{name: 'name'}]
                                },
                                {
                                    type: 'entity',
                                    name: 'cm.ContractAgent#contract',
                                    as: 'mca',
                                    joinType: 'left',
                                    fields: [{name: 'id'}],
                                    filterCriteria: [
                                        FilterCriteria.create('mca.assignment', FilterCriteria.cOperatorEqual,
                                            ContractAgentEnum.main)
                                    ],
                                    joins: [{
                                        type: 'entity', name: 'agent', as: 'mag', joinType: 'left',
                                            fields: [{name: 'id'}, {name: 'name'}]
                                    }]
                                }
                            ]
                        }
                    ]
                },
                filters: [FilterCriteria.create('e.id', FilterCriteria.cOperatorEqual, contractRunId)]
            })
            .query({
                name: 'cpRunReviewContractRunMessages',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cp.ProvisionContractRunMessage',
                    as: 'e',
                    joins: [
                        {
                            type: 'entity', name: 'provisionContractRun', as: 'pcr', joinType: 'left',
                            fields: [{name: 'id'}],
                            joins: [
                                {
                                    type: 'entity', name: 'contract', as: 'c', joinType: 'left',
                                    fields: [{name: 'id'}, {name: 'contractIdentifier'}],
                                    joins: [{
                                        type: 'entity',
                                        name: 'cm.ContractAgent#contract',
                                        as: 'mca',
                                        joinType: 'left',
                                        fields: [{name: 'id'}],
                                        filterCriteria: [
                                            FilterCriteria.create('mca.assignment', FilterCriteria.cOperatorEqual,
                                                ContractAgentEnum.main)
                                        ],
                                        joins: [{
                                            type: 'entity', name: 'agent', as: 'mag', joinType: 'left',
                                                fields: [{name: 'id'}, {name: 'name'}]
                                        }]
                                    }]
                                }
                            ]
                        },
                        {
                            type: 'entity', name: 'provisionContract', as: 'pc', joinType: 'left',
                            fields: [{name: 'startDate'}],
                            joins: [
                                {type: 'entity', name: 'provisionTypeContractEvent', as: 'pce', joinType: 'left',
                                    fields: [{name: 'name'}]}
                            ]
                        }
                    ]
                },
                filters: [FilterCriteria.create('e.provisionContractRunId', FilterCriteria.cOperatorEqual,
                    contractRunId)],
                sortings: [new Sorting('e.severity', false), new Sorting('e.id', true)]
            })
            .execute()
            .pipe(
                map(result => {
                    this.assertOk(result, runQuery);
                    this.assertOk(result, messageQuery);
                    const rows = result.get(runQuery);
                    if (!rows.length) {
                        throw new Error('Contract run ' + contractRunId + ' not found');
                    }
                    const first = rows[0];
                    const view: CpRunContractRunView = {
                        id: this.num(first['e.id']),
                        status: this.num(first['e.status']),
                        contractId: this.num(first['c.id'] ?? first['e.contractId']),
                        contractIdentifier: first['c.contractIdentifier'] ?? null,
                        contractTypeName: first['ct.name'] ?? null,
                        mainAgents: this.distinctAgents(rows.map(row => ({
                            agentId: this.num(row['mag.id']),
                            agentName: row['mag.name'] ?? ''
                        }))),
                        messages: this.toContractMessageViews(result.get(messageQuery))
                    };
                    return view;
                }),
                switchMap(view => this.attachContractRunExtras(view))
            );
    }

    // ── Transaction totals ─────────────────────────────────────────────────────────────────────

    /**
     * Sum and count of all bookings of one run, in total and per agent — the "Summenzeile" of the
     * transaction tabs. Two `groupBy` queries in ONE round-trip, filtered exactly like the tables
     * (`provisionRunId = run`), so the totals always match the rows the table lists.
     *
     * For contract bookings the agent is reached over the reconcile anchor
     * `provisionContractAgent.provisionAgent.agent`; for account bookings over `provisionAgent.agent`.
     */
    loadTransactionTotals(provisionRunId: number, kind: CpRunTransactionKind): Observable<CpRunTransactionTotals> {

        const entity = kind === 'contract' ? 'cp.ProvisionContractTransaction' : 'cp.ProvisionAgentTransaction';
        const totalQuery = 'cpRunReviewTotal_' + kind;
        const perAgentQuery = 'cpRunReviewPerAgent_' + kind;
        return this.qlService.builder()
            .showErrors(false)
            .query({
                name: totalQuery,
                type: 'view',
                start: {
                    type: 'groupBy', name: entity, as: 'e',
                    fields: [
                        {name: '*', function: 'count', as: 'cnt'},
                        {name: 'amount', function: 'sum', as: 'total'}
                    ]
                },
                filters: [FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual, provisionRunId)]
            })
            .query({
                name: perAgentQuery,
                type: 'view',
                start: {
                    type: 'groupBy', name: entity, as: 'e',
                    fields: [
                        {name: '*', function: 'count', as: 'cnt'},
                        {name: 'amount', function: 'sum', as: 'total'}
                    ],
                    // Contract bookings reach the agent over provisionContractAgent, account bookings directly.
                    joins: kind === 'contract'
                        ? [{
                            type: 'groupBy', name: 'provisionContractAgent', as: 'pca',
                            joins: [{
                                type: 'groupBy', name: 'provisionAgent', as: 'pa', fields: [{name: 'id'}],
                                joins: [{type: 'groupBy', name: 'agent', as: 'ag', fields: [{name: 'id'},
                                    {name: 'name'}]}]
                            }]
                        }]
                        : [{
                            type: 'groupBy', name: 'provisionAgent', as: 'pa', fields: [{name: 'id'}],
                            joins: [{type: 'groupBy', name: 'agent', as: 'ag', fields: [{name: 'id'}, {name: 'name'}]}]
                        }]
                },
                filters: [FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual, provisionRunId)]
            })
            .execute()
            .pipe(map(result => {
                this.assertOk(result, totalQuery);
                this.assertOk(result, perAgentQuery);
                const totalRow = result.get(totalQuery)[0] ?? {};
                const perAgent: CpRunAgentTotal[] = result.get(perAgentQuery)
                    .map(row => ({
                        agentId: this.num(row['ag.id']),
                        agentName: row['ag.name'] ?? 'Ohne Berater',
                        provisionAgentId: this.num(row['pa.id']),
                        count: this.num(row['e.cnt']) ?? 0,
                        total: Number(row['e.total'] ?? 0)
                    }))
                    .sort((a, b) => b.total - a.total);
                return {
                    count: this.num(totalRow['e.cnt']) ?? 0,
                    total: Number(totalRow['e.total'] ?? 0),
                    perAgent
                };
            }));
    }

    // ── Contract behind an event / rate row ────────────────────────────────────────────────────

    /**
     * The `cm.Contract` (id + VSNR) behind one `cp.ProvisionContract` or `cp.ProvisionContractAgent`
     * row — both carry a `contract` FK.
     */
    loadRowContract(objectType: string, objectId: number): Observable<CpRunRowContract | null> {

        const queryName = 'cpRunReviewRowContract';

        return this.execute({
            name: queryName,
            type: 'view',
            start: {
                type: 'entity', name: objectType, as: 'e', fields: [{name: 'id'}],
                joins: [{type: 'entity', name: 'contract', as: 'c', joinType: 'left', fields: [{name: 'id'},
                    {name: 'contractIdentifier'}]}]
            },
            filters: [FilterCriteria.create('e.id', FilterCriteria.cOperatorEqual, objectId)]
        }, queryName).pipe(map(rows => {
            const contractId = rows.length ? this.num(rows[0]['c.id']) : null;
            return contractId ? {contractId,
                contractIdentifier: rows[0]['c.contractIdentifier'] ?? String(contractId)} : null;
        }));
    }

    // ── Agents + accounts ──────────────────────────────────────────────────────────────────────

    /**
     * Names and Abrechnungskonten of a set of agents. An agent without an account comes back with
     * `provisionAgentId = null` — that is the M-004 fact and must stay visible, not be dropped.
     */
    loadAgentRefs(agents: { agentId: number, agentName?: string }[]): Observable<CpRunAgentRef[]> {

        const ids = Array.from(new Set(agents.map(agent => agent.agentId).filter(id => !!id)));

        if (!ids.length) {
            return of([]);
        }

        const agentQuery = 'cpRunReviewAgents';
        const accountQuery = 'cpRunReviewAccounts';

        return this.qlService.builder()
            .showErrors(false)
            .query({
                name: agentQuery,
                type: 'view',
                start: {type: 'entity', name: 'am.Agent', as: 'e', fields: [{name: 'id'}, {name: 'name'}]},
                filters: [FilterCriteria.create('e.id', FilterCriteria.cOperatorIn, ids)]
            })
            .query({
                name: accountQuery,
                type: 'view',
                start: {
                    type: 'entity', name: 'cp.ProvisionAgent', as: 'e',
                    fields: [{name: 'id'}, {name: 'agentId'}, {name: 'entityStatus'}]
                },
                filters: [FilterCriteria.create('e.agentId', FilterCriteria.cOperatorIn, ids)],
                sortings: [new Sorting('e.id', false)]
            })
            .execute()
            .pipe(map(result => {
                this.assertOk(result, agentQuery);
                this.assertOk(result, accountQuery);

                const names = new Map<number, string>();
                result.get(agentQuery).forEach(row => names.set(this.num(row['e.id']), row['e.name']));

                // An active account wins over an inactive one of the same agent.
                const accounts = new Map<number, { id: number, active: boolean }>();
                result.get(accountQuery).forEach(row => {
                    const agentId = this.num(row['e.agentId']);
                    const candidate = {id: this.num(row['e.id']), active: this.isActive(row['e.entityStatus'])};
                    const existing = accounts.get(agentId);
                    if (!existing || (!existing.active && candidate.active)) {
                        accounts.set(agentId, candidate);
                    }
                });

                return ids.map(agentId => {
                    const given = agents.find(agent => agent.agentId === agentId);
                    const account = accounts.get(agentId);
                    return {
                        agentId,
                        agentName: names.get(agentId) ?? given?.agentName ?? '',
                        provisionAgentId: account?.id ?? null,
                        provisionAgentActive: account ? account.active : false
                    };
                });
            }));
    }

    // ── internals ──────────────────────────────────────────────────────────────────────────────

    /**
     * Collapse the joined rows (one per message × Hauptvermittler) into one view per message, the
     * order of the query preserved.
     */
    private toContractMessageViews(rows: any[]): CpRunMessageView[] {

        const views = new Map<number, CpRunMessageView>();
        const contractAgents = new Map<number, { agentId: number, agentName: string }[]>();

        for (const row of rows) {
            const id = this.num(row['e.id']);
            if (!views.has(id)) {
                const view = this.toMessageBase(row);
                view.contractId = this.num(row['c.id']);
                view.contractIdentifier = row['c.contractIdentifier'] ?? null;
                view.eventName = row['pce.name'] ?? null;
                view.eventStartDate = row['pc.startDate'] ?? null;
                views.set(id, view);
                contractAgents.set(id, []);
            }
            const agentId = this.num(row['mag.id']);
            if (agentId) {
                contractAgents.get(id).push({agentId, agentName: row['mag.name'] ?? ''});
            }
        }

        // Until attachMessageAgents() resolves accounts, the contract's Hauptvermittler are carried
        // as account-less refs; only the single-message path enriches them.
        views.forEach((view, id) => {
            view.agents = this.distinctAgents(contractAgents.get(id));
            view.agentSource = view.agents.length ? 'contract' : 'none';
        });

        return Array.from(views.values());
    }

    /** The message fields every message view shares. */
    private toMessageBase(row: any): CpRunMessageView {
        return {
            id: this.num(row['e.id']),
            severity: this.toSeverity(row['e.severity']),
            messageCode: row['e.messageCode'] ?? '',
            message: row['e.message'] ?? '',
            detailedMessage: row['e.detailedMessage'] ?? null,
            obsolete: row['e.obsolete'] === true,
            createdDate: row['e.createdDate'] ?? null,
            contractId: null,
            contractIdentifier: null,
            eventName: null,
            eventStartDate: null,
            agentSource: 'none',
            agents: []
        };
    }

    /**
     * Resolve the agents of one message to name + account: the agent named in `detailedMessage`
     * when there is one, otherwise the contract's Hauptvermittler collected by the query.
     */
    private attachMessageAgents(view: CpRunMessageView): Observable<CpRunMessageView> {

        const namedAgentId = parseCpRunMessageAgentId(view.detailedMessage);

        const candidates = namedAgentId
            ? [{agentId: namedAgentId}]
            : view.agents.map(agent => ({agentId: agent.agentId, agentName: agent.agentName}));

        if (namedAgentId) {
            view.agentSource = 'message';
        }

        return this.loadAgentRefs(candidates).pipe(map(agents => {
            view.agents = agents;
            if (!agents.length) {
                view.agentSource = 'none';
            }
            return view;
        }));
    }

    /**
     * Accounts of the Hauptvermittler. The bookings of the contract in this run are NOT loaded here:
     * the detail's bookings widget is their single loader and reports count and sum via onLoadData.
     */
    private attachContractRunExtras(view: CpRunContractRunView): Observable<CpRunContractRunView> {
        return this.loadAgentRefs(view.mainAgents).pipe(
            map(agents => {
                view.mainAgents = agents;
                return view;
            })
        );
    }

    /** Run ONE query and hand back its rows; a failed pipe becomes an error, never an empty list. */
    private execute(query: QlQueryDto, queryName: string): Observable<any[]> {
        return this.qlService.builder()
            .showErrors(false)
            .query(query)
            .execute()
            .pipe(map(result => {
                this.assertOk(result, queryName);
                return result.get(queryName);
            }));
    }

    /** A pipe with status != 0 failed on the server — surface it as an error state. */
    private assertOk(result: QlQueryResult, queryName: string): void {
        const pipe = result.getPipeResult(queryName);
        if (!pipe || (pipe.status !== undefined && pipe.status !== null && pipe.status !== 0)) {
            throw new Error('QL query ' + queryName + ' failed');
        }
    }

    private distinctAgents(agents: { agentId: number, agentName: string }[]): CpRunAgentRef[] {
        const seen = new Set<number>();
        const result: CpRunAgentRef[] = [];
        for (const agent of agents ?? []) {
            if (agent.agentId && !seen.has(agent.agentId)) {
                seen.add(agent.agentId);
                result.push({agentId: agent.agentId, agentName: agent.agentName, provisionAgentId: null,
                    provisionAgentActive: false});
            }
        }
        return result;
    }

    /** Severity arrives as ordinal; tolerate the enum name as well (see CpConfigCheckPanel). */
    private toSeverity(value: any): ProvisionMessageSeverity {
        if (typeof value === 'number') {
            return value;
        }
        const named: ProvisionMessageSeverity | undefined =
            ProvisionMessageSeverity[String(value ?? '').toUpperCase() as keyof typeof ProvisionMessageSeverity];
        return named ?? ProvisionMessageSeverity.INFO;
    }

    private isActive(entityStatus: any): boolean {
        return entityStatus === EntityStatusEnum.active || entityStatus === 'active';
    }

    private num(value: any): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
}
